import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import toast from 'react-hot-toast';

const DIMENSIONS = {
  account: { label: 'Account', endpoint: '/account-ledger', paramKey: 'account_id', responseKey: 'account_ledger' },
  fund: { label: 'Fund', endpoint: '/fund-ledger', paramKey: 'fund_id', responseKey: 'fund_ledger' },
  cost_center: { label: 'Cost Center', endpoint: '/cost-center-ledger', paramKey: 'cost_center_id', responseKey: 'cost_center_ledger' },
};

const PAGE_SIZE = 50;

const Ledgers = () => {
  const [searchParams] = useSearchParams();
  const initialAccountId = searchParams.get('account_id') || '';
  const initialFundId = searchParams.get('fund_id') || '';
  const initialCostCenterId = searchParams.get('cost_center_id') || '';
  const initialDimension = initialAccountId ? 'account' : initialFundId ? 'fund' : initialCostCenterId ? 'cost_center' : 'account';
  const initialEntityId = initialAccountId || initialFundId || initialCostCenterId;

  const [dimension, setDimension] = useState(initialDimension);
  const [accounts, setAccounts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  const [entityId, setEntityId] = useState(initialEntityId);
  const [fromDate, setFromDate] = useState(searchParams.get('from_date') || '');
  const [toDate, setToDate] = useState(searchParams.get('to_date') || '');
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [accRes, fundsRes, ccRes] = await Promise.all([
          accountsApi.get('/chart-of-accounts'),
          accountsApi.get('/funds'),
          accountsApi.get('/cost-centers', { params: { is_active: true } }),
        ]);
        setAccounts(accRes.data.accounts.filter(a => a.is_active));
        setFunds(fundsRes.data.funds);
        setCostCenters(ccRes.data.cost_centers);
      } catch (err) {
        toast.error('Failed to load filter options');
      }
    };
    loadRefs();
  }, []);

  // Skip the very first run so an incoming link (e.g. from Cash & Bank
  // Position) that pre-selects a dimension doesn't immediately get wiped by
  // this same reset logic reacting to that initial value as a "change".
  const isFirstDimensionRender = useRef(true);
  useEffect(() => {
    if (isFirstDimensionRender.current) {
      isFirstDimensionRender.current = false;
      return;
    }
    setEntityId('');
    setData(null);
    setPage(1);
  }, [dimension]);

  useEffect(() => {
    if (initialEntityId) runReport(1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runReport = async (targetPage = 1) => {
    if (!entityId) return toast.error(`Select ${DIMENSIONS[dimension].label.toLowerCase()} first`);
    const cfg = DIMENSIONS[dimension];
    try {
      setLoading(true);
      const params = { [cfg.paramKey]: entityId, page: targetPage, page_size: PAGE_SIZE };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const response = await accountsApi.get(cfg.endpoint, { params });
      setData(response.data[cfg.responseKey]);
      setPage(targetPage);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const entityOptions = dimension === 'account' ? accounts : dimension === 'fund' ? funds : costCenters;
  const totalPages = data ? Math.max(1, Math.ceil(data.total_count / PAGE_SIZE)) : 1;

  return (
    <Layout title="Ledgers">
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ledger Type</label>
            <select value={dimension} onChange={(e) => setDimension(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {Object.entries(DIMENSIONS).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{DIMENSIONS[dimension].label}</label>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[220px]">
              <option value="">Select...</option>
              {entityOptions.map(o => (
                <option key={o.id} value={o.id}>
                  {dimension === 'account' ? `${o.account_code} — ${o.account_name}` : `${o.code} — ${o.name}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={() => runReport(1)} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            Apply
          </button>
        </div>
      </div>

      {data?.note && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mb-4">
          {data.note}
        </div>
      )}

      {data && (
        <div className="bg-white rounded-xl shadow p-4 mb-4 grid grid-cols-4 gap-4 text-sm">
          <div><div className="text-gray-500 text-xs">Opening Balance</div><div className="font-semibold text-gray-800">{Number(data.opening_balance).toFixed(2)}</div></div>
          <div><div className="text-gray-500 text-xs">Closing Balance</div><div className="font-semibold text-gray-800">{Number(data.closing_balance).toFixed(2)}</div></div>
          <div><div className="text-gray-500 text-xs">Period Debit</div><div className="font-semibold text-gray-800">{Number(data.period_debit).toFixed(2)}</div></div>
          <div><div className="text-gray-500 text-xs">Period Credit</div><div className="font-semibold text-gray-800">{Number(data.period_credit).toFixed(2)}</div></div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Date</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Entry No</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Voucher</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Description</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Debit</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Credit</th>
              <th className="text-right px-4 py-3 text-gray-600 font-semibold">Running Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : !data ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Select a {DIMENSIONS[dimension].label.toLowerCase()} and click Apply</td></tr>
            ) : data.lines.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">No activity in this range</td></tr>
            ) : data.lines.map(l => (
              <tr key={l.line_id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-600">{l.entry_date?.slice(0, 10)}</td>
                <td className="px-4 py-2 text-gray-600">{l.entry_no}</td>
                <td className="px-4 py-2 text-gray-600">
                  {l.voucher_id ? <Link to={`/vouchers/${l.voucher_id}`} className="text-blue-700 hover:underline">{l.voucher_no}</Link> : '—'}
                </td>
                <td className="px-4 py-2 text-gray-700">{l.description || '—'}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(l.debit) > 0 ? Number(l.debit).toFixed(2) : ''}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(l.credit) > 0 ? Number(l.credit).toFixed(2) : ''}</td>
                <td className="px-4 py-2 text-right font-medium text-gray-800">{Number(l.running_balance).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total_count > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
          <span>Page {page} of {totalPages} ({data.total_count} lines)</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => runReport(page - 1)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm disabled:opacity-40 hover:bg-gray-50">Previous</button>
            <button disabled={page >= totalPages} onClick={() => runReport(page + 1)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Ledgers;
